import { AxiosCalls } from "./Functions/AxiosCalls";
import { handleDefaultApiErrors } from "Utils/Global/apiErrorHandling";
import type {
  IProductData,
  ProductSpec,
  IHandleGetProductBySlugParams,
  GetBySlugResponseData,
  AddProductRequestDTO,
  EditProductRequestDTO,
  IHandleDeleteProductParams,
  EditProductFormValues,
  PreparedEditProductState,
  ApplyPreparedEditProductStateParams,
  IHandleInvalidEditProductFormParams,
  IDeleteProductImageParams,
  IProductImageProps,
  IConfirmImageDeletionParams,
  HandleEditProductSubmissionParams,
  ResetEditProductFormParams,
  IDeleteTechnicalSheetClientParams,
} from "@model/Products.model";

import {
  IHandleCancelCreateProductParams,
  IHandleInvalidCreateProductFormParams,
  IHandleCreateProductParams,
} from "@model/Category.model";
import { ExistingUploadFile } from "@model/UIComponents.model";

export class ProductsService {
  async handleGetProductBySlug({
    slug,
    setLoading,
    setApiError,
    setProduct,
  }: IHandleGetProductBySlugParams): Promise<void> {
    const cleanSlug = (slug ?? "").trim();
    if (!cleanSlug) {
      setApiError(null);
      setProduct(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Get<GetBySlugResponseData>(
        `/category/getProductSlug/${encodeURIComponent(cleanSlug)}`,
      );

      const productresponse = resp?.data?.product ?? null;

      if (!productresponse) {
        setProduct(null);
        return;
      }

      const specs: ProductSpec[] = Array.isArray(productresponse.specs)
        ? productresponse.specs.map((s) => ({
            label: String(s?.label ?? "").trim(),
            value: String(s?.value ?? "").trim(),
          }))
        : [];
      const images: IProductImageProps[] = Array.isArray(productresponse.images)
        ? productresponse.images
            .map((img) => ({
              id: String(img?.id ?? "").trim(),
              url: String(img?.url ?? "").trim(),
              resolution: img?.resolution ?? null,
              sizeBytes: img?.sizeBytes ?? null,
            }))
            .filter((x) => Boolean(x.id) && Boolean(x.url))
        : [];

      const product: IProductData = {
        id: String(productresponse.id ?? ""),
        slug: String(productresponse.slug ?? ""),
        name: String(productresponse.name ?? "Untitled"),
        price:
          typeof productresponse.price === "number" ? productresponse.price : 0,
        currency: "USD",
        shortDescription: String(productresponse.shortDescription ?? ""),
        longDescription: String(productresponse.longDescription ?? ""),
        images,
        categoryId: String(productresponse.category.id ?? ""),
        imageUrl: images[0]?.url,
        files: productresponse.files ?? [],
        stock:
          typeof productresponse.stock === "number" ? productresponse.stock : 0,
        specs,
        maxPerUser:
          typeof productresponse.maxPerUser === "number"
            ? productresponse.maxPerUser
            : undefined,
      };

      setProduct(product);
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }

  filterProductsBySearch(
    products: IProductData[],
    searchQuery: string,
  ): IProductData[] {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return products;

    return products.filter((p) => {
      const name = (p.name ?? "").toLowerCase();
      const short = (p.shortDescription ?? "").toLowerCase();
      return name.includes(q) || short.includes(q);
    });
  }

  async handleCreateProduct({
    data,
    categoryId,
    token,
    setLoading,
    setApiError,
    setSuccessMsg,
    onResetUI,
    onAuthError,
    onForbidden,
  }: IHandleCreateProductParams): Promise<void> {
    setApiError(null);

    if (!categoryId) {
      setApiError("Missing categoryId.");
      return;
    }

    setLoading(true);

    try {
      const payload: any = {
        name: (data.name ?? "").trim(),
        price: data.price,
        shortDescription: (data.shortDescription ?? "").trim(),
        longDescription: (data.longDescription ?? "").trim(),
        stock: data.stock,
        specs: JSON.stringify(data.specs ?? []),
        images: data.images,
        categoryId,
      };

      const technicalSheet = data.files;
      if (technicalSheet && technicalSheet.length > 0) {
        payload.filesPdf = technicalSheet;
      }

      await AxiosCalls.Post<void, AddProductRequestDTO>(
        "/category/add-product",
        payload,
        token,
      );

      setSuccessMsg("Product created successfully.");
      onResetUI();
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  handleInvalidCreateProductForm({
    setApiError,
    setSuccessMsg,
  }: IHandleInvalidCreateProductFormParams): void {
    setSuccessMsg(null);
    setApiError("Please fix the errors and try again.");
  }

  handleCancelCreateProduct({
    onResetUI,
    setApiError,
    setSuccessMsg,
  }: IHandleCancelCreateProductParams): void {
    onResetUI();
    setApiError(null);
    setSuccessMsg(null);
  }
  async handleDeleteProduct({
    productId,
    token,
    setLoading,
    setApiError,
    onSuccess,
    onAuthError,
    onForbidden,
  }: IHandleDeleteProductParams): Promise<void> {
    setApiError(null);

    if (!productId) {
      setApiError("Missing productId.");
      return;
    }

    setLoading(true);

    try {
      await AxiosCalls.Delete<void>(
        `/category/delete-product/${encodeURIComponent(productId)}`,
        token,
      );

      onSuccess?.("Product deleted successfully.");
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }
  private fileNameFromUrl(url: string): string {
    const clean = (url ?? "").split("?")[0].split("#")[0];
    const last = clean.split("/").pop() ?? "";
    return decodeURIComponent(last) || "file";
  }

  prepareEditProductFormState({
    product,
    defaultValues,
    buildImageUrl,
    buildPdfUrl,
  }: {
    product: IProductData;
    defaultValues: EditProductFormValues;
    buildImageUrl: (u?: string | null) => string | null;
    buildPdfUrl?: (u?: string | null) => string | null;
  }): PreparedEditProductState {
    const specs = product.specs?.length
      ? product.specs
      : [{ label: "", value: "" }];

    const existingImages = (product.images ?? [])
      .map((img) => {
        const built = buildImageUrl(img.url);
        if (!built) return null;

        return {
          id: img.id,
          name: this.fileNameFromUrl(built),
          url: built,
          type: "image/png",
        } as ExistingUploadFile;
      })
      .filter(Boolean) as ExistingUploadFile[];
    // const pdfPath = String(product?.technicalSheetUrl ?? "").trim();

    // const pdfUrl = pdfPath ? (buildPdfUrl ?? buildImageUrl)(pdfPath) : null;

    const existingPdf: ExistingUploadFile[] = [];

    return {
      product,
      formValues: {
        ...defaultValues,
        categoryId: product.categoryId ?? "",
        name: product.name ?? "",
        price: (product.price ?? "") as any,
        stock: (product.stock ?? "") as any,
        shortDescription: product.shortDescription ?? "",
        longDescription: product.longDescription ?? "",
        specs,
        images: null,
        technicalSheet: null,
      },
      existingImages,
      existingPdf,
    };
  }
  applyPreparedEditProductState(
    args: ApplyPreparedEditProductStateParams,
  ): void {
    const {
      state,
      reset,
      resetSpecs,
      setLoadedProduct,
      setExistingImageFiles,
      setExistingPdfFiles,
      setRemovedExistingImageIndexes,
      setIsExistingPdfRemoved,
      clearMessages,
      setDidSave,
    } = args;

    setLoadedProduct(state.product);
    reset(state.formValues);
    resetSpecs(state.formValues.specs);
    setExistingImageFiles(state.existingImages);
    setExistingPdfFiles(state.existingPdf);
    setRemovedExistingImageIndexes([]);
    setIsExistingPdfRemoved(false);
    clearMessages();
    setDidSave(false);
  }

  handleInvalidEditProductForm({
    setApiError,
    setSuccessMsg,
  }: IHandleInvalidEditProductFormParams): void {
    setSuccessMsg(null);
    setApiError("Please fix the errors and try again.");
  }

  async handleDeleteProductImage({
    imageId,
    token,
    setLoading,
    setApiError,
    onAuthError,
    onForbidden,
  }: IDeleteProductImageParams): Promise<void> {
    setLoading?.(true);
    setApiError?.(null);

    if (!imageId) {
      setApiError?.("Missing imageId.");
      setLoading?.(false);
      return;
    }

    try {
      await AxiosCalls.Delete<void>(
        `/category/products/images/${encodeURIComponent(imageId)}`,
        token,
      );
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError?.(msg);
    } finally {
      setLoading?.(false);
    }
  }

  resetEditProductForm(params: ResetEditProductFormParams): void {
    const {
      didSave,
      loadedProduct,
      defaultValues,
      buildImageUrl,
      buildPdfUrl,
      reset,
      resetSpecs,
      setExistingImageFiles,
      setExistingPdfFiles,
      setRemovedExistingImageIndexes,
      setIsExistingPdfRemoved,
      clearMessages,
    } = params;

    clearMessages();

    if (didSave) {
      setRemovedExistingImageIndexes([]);
      setIsExistingPdfRemoved(false);
      return;
    }

    if (!loadedProduct) return;

    const state = this.prepareEditProductFormState({
      product: loadedProduct,
      defaultValues,
      buildImageUrl,
      buildPdfUrl,
    });

    reset(state.formValues);
    resetSpecs(state.formValues.specs);
    setExistingImageFiles(state.existingImages);
    setExistingPdfFiles(state.existingPdf);
    setRemovedExistingImageIndexes([]);
    setIsExistingPdfRemoved(false);
  }

  async handleEditProductSubmission({
    formValues,
    loadedProductId,
    authToken,
    setLoading,
    setApiError,
    setSuccessMsg,
    onAuthError,
    onForbidden,
    setDidSave,
  }: HandleEditProductSubmissionParams): Promise<void> {
    setApiError(null);
    setSuccessMsg(null);

    const productId = String(loadedProductId ?? "").trim();
    if (!productId) {
      setApiError("Product not loaded yet.");
      return;
    }
    setLoading(true);

    try {
      const formData = new FormData();
 
      // scalar fields
      formData.append("name", (formValues.name ?? "").trim());
      formData.append("price", String(formValues.price));
      formData.append("shortDescription", (formValues.shortDescription ?? "").trim());
      formData.append("longDescription", (formValues.longDescription ?? "").trim());
      formData.append("stock", String(formValues.stock));
      formData.append("specs", JSON.stringify(formValues.specs ?? []));
      formData.append("categoryId", formValues.categoryId);
   
      // images — optional on edit
      if (formValues.images && formValues.images.length > 0) {
        Array.from(formValues.images).forEach((file) => {
          formData.append("images", file);
        });
      }
   
      // labeled pdf files
      if (formValues.files && formValues.files.length > 0) {
        formValues.files.forEach((item, index) => {
          formData.append("filesPdf", item.file);
          formData.append(`filesPdfLabel`, item.fileLabel);
        });
      }
      
      await AxiosCalls.Put<void, any>(
        `/category/product/${encodeURIComponent(productId)}`,
        formData,
        authToken,
      );

      setSuccessMsg("Product updated successfully.");
      setDidSave(true);
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(authToken),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  confirmImageDeletion({
    imageIndex,
    productImages,
    setApiError,
    setSuccessMsg,
    setPendingDeleteImage,
    setIsConfirmOpen,
  }: IConfirmImageDeletionParams) {
    if (productImages.length <= 1) {
      setSuccessMsg(null);
      setApiError && setApiError("You must keep at least 1 image.");
      return;
    }

    const file = productImages[imageIndex];
    if (!file) return;

    setPendingDeleteImage(file);
    setIsConfirmOpen(true);
  }

  async handleDeleteTechnicalSheet({
    loadedProductId,
    token,
    setLoading,
    setApiError,
    onAuthError,
    onForbidden,
    setExistingPdfFiles,
    setIsExistingPdfRemoved,
    setValue,
    setIsTechSheetConfirmOpen,
  }: IDeleteTechnicalSheetClientParams): Promise<void> {
    const productId = String(loadedProductId ?? "").trim();
    if (!productId) return;

    setLoading(true);
    setApiError(null);

    try {
      await AxiosCalls.Delete<void>(
        `/category/products/technical-sheet/${encodeURIComponent(productId)}`,
        token,
      );

      setExistingPdfFiles([]);
      setIsExistingPdfRemoved(true);
      setValue("technicalSheet", null);

      setIsTechSheetConfirmOpen(false);
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }
}
